/**
 * Hook for managing signals enablement status and countdown
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";
import { getApiErrorDisplay } from "../utils/apiErrorDisplay";
import { presentMutationError } from "../utils/showApiErrorToast";
import {
  enableSignals,
  disableSignals,
  getSignalsStatus,
  SignalStatusResponse,
} from "../services/signalsApi";

export const useSignals = () => {
  const { isLoaded: isAuthLoaded, isSignedIn } = useAuth();
  const [signalsEnabled, setSignalsEnabled] = useState<boolean>(false);
  const [enabledUntil, setEnabledUntil] = useState<Date | null>(null);
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState<number | null>(null);
  const [websocketPath, setWebsocketPath] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Format time remaining as HH:MM:SS or MM:SS
   */
  const formatTimeRemaining = useCallback((seconds: number | null): string => {
    if (seconds === null || seconds <= 0) {
      return "00:00";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  /**
   * Update state from API response
   */
  const updateStateFromResponse = useCallback((response: SignalStatusResponse) => {
    setSignalsEnabled(response.signals_enabled);
    setEnabledUntil(response.enabled_until ? new Date(response.enabled_until) : null);
    setTimeRemainingSeconds(response.time_remaining_seconds);
    // Only update websocket_path when the backend actually provides it.
    if (response.websocket_path !== undefined) {
      setWebsocketPath(response.websocket_path);
    }
    setError(null);
  }, []);

  /**
   * Stop countdown timer
   */
  const stopCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  /**
   * Fetch signals status once (no polling). Used on init and after enable/disable.
   */
  const checkStatus = useCallback(async () => {
    try {
      const response = await getSignalsStatus();
      updateStateFromResponse(response);

      // Auto-disable if time remaining reaches 0 or becomes null
      if (response.signals_enabled && (response.time_remaining_seconds === null || response.time_remaining_seconds <= 0)) {
        setSignalsEnabled(false);
        setEnabledUntil(null);
        setTimeRemainingSeconds(null);
        setWebsocketPath(null);
        stopCountdown();
      }
    } catch (err: any) {
      console.error("Failed to check signals status:", err);
      setError(err.message || "Failed to check signals status");
    }
  }, [updateStateFromResponse, stopCountdown]);

  /**
   * Start countdown timer (local only; no server polling)
   */
  const startCountdown = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setTimeRemainingSeconds((prev) => {
        if (prev === null || prev <= 1) {
          setSignalsEnabled(false);
          setEnabledUntil(null);
          stopCountdown();
          setWebsocketPath(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [stopCountdown]);

  /**
   * Enable signals
   */
  const handleEnableSignals = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await enableSignals();
      // Diagnostics: confirm backend response fields (including websocket_path if present)
      console.log("[signals] POST /v1/signals/enable response:", response);
      updateStateFromResponse(response);

      if (response.signals_enabled) {
        startCountdown();
        toast.success("Signals Enabled", {
          description: "You will now receive trading signals for the next 3 hours.",
        });
      }
    } catch (err: unknown) {
      const display = getApiErrorDisplay(err, "Failed to enable signals");
      setError(display.message);
      presentMutationError(err, "Failed to Enable Signals");
    } finally {
      setIsLoading(false);
    }
  }, [updateStateFromResponse, startCountdown]);

  /**
   * Disable signals
   */
  const handleDisableSignals = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await disableSignals();
      updateStateFromResponse(response);

      stopCountdown();
      // When disabling, ensure websocket path is cleared so WS hook doesn't
      // reconnect using an old path.
      setWebsocketPath(null);

      toast.info("Signals Disabled", {
        description: "Signal streaming has been stopped.",
      });
    } catch (err: unknown) {
      const display = getApiErrorDisplay(err, "Failed to disable signals");
      setError(display.message);
      presentMutationError(err, "Failed to Disable Signals");
    } finally {
      setIsLoading(false);
    }
  }, [updateStateFromResponse, stopCountdown]);

  /**
   * One-time status fetch when auth is ready / user signs in.
   * Restores signals_enabled state after refresh so WebSocket can connect; no polling.
   */
  useEffect(() => {
    if (!isAuthLoaded) return;
    if (!isSignedIn) {
      stopCountdown();
      setSignalsEnabled(false);
      setEnabledUntil(null);
      setTimeRemainingSeconds(null);
      setWebsocketPath(null);
      return;
    }
    checkStatus();

    return () => {
      stopCountdown();
    };
  }, [isAuthLoaded, isSignedIn, checkStatus, stopCountdown]);

  /**
   * Start/stop local countdown when signals enabled state changes (e.g. after refresh or enable).
   * No server polling; WebSocket pushes updates from backend.
   */
  useEffect(() => {
    if (signalsEnabled && timeRemainingSeconds !== null && timeRemainingSeconds > 0) {
      startCountdown();
    } else {
      stopCountdown();
    }

    return () => {
      stopCountdown();
    };
  }, [signalsEnabled, startCountdown, stopCountdown]);

  return {
    signalsEnabled,
    enabledUntil,
    timeRemainingSeconds,
    websocketPath,
    isLoading,
    error,
    enableSignals: handleEnableSignals,
    disableSignals: handleDisableSignals,
    checkStatus,
    formatTimeRemaining,
  };
};
